import type { WordCardKind } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

import type { CardContent, CardContentKind } from "#card-content";

/**
 * Translate between the `user_word_sentence_card.kind` column and {@link CardContentKind}.
 *
 * Two spellings exist deliberately. The column value is already scoped by its table, so it reads
 * `characters-in-context`; the card-content union spans graphemes too, so it reads
 * `word-characters-in-context` to stay `<subject>-<aspect>` like the grapheme pair. Keeping the
 * translation in ONE exhaustive switch (rather than letting callers concatenate a `word-` prefix)
 * means the two vocabularies cannot drift: adding a kind to either side fails to compile here.
 */
export function cardContentKindFromWordCardKind(kind: WordCardKind): CardContentKind {
  switch (kind) {
    case "characters-in-context": {
      return "word-characters-in-context";
    }
    case "characters-in-sentence": {
      return "word-characters-in-sentence";
    }
    case "pronunciation-in-sentence": {
      return "word-pronunciation-in-sentence";
    }
  }
}

/**
 * Build the {@link CardContent} for a word overlay row.
 *
 * Written as an exhaustive switch that spells out each member rather than computing `kind` and
 * spreading the shared fields: `CardContent` is a discriminated union, and TypeScript cannot narrow
 * a member whose discriminant is a computed `CardContentKind` — the object would have to be cast,
 * which is exactly the check worth keeping.
 */
export function wordCardContent({
  kind,
  pinyin,
  word,
}: {
  kind: WordCardKind;
  pinyin: string;
  word: string;
}): CardContent {
  switch (kind) {
    case "characters-in-context": {
      return { kind: "word-characters-in-context", pinyin, word };
    }
    case "characters-in-sentence": {
      return { kind: "word-characters-in-sentence", pinyin, word };
    }
    case "pronunciation-in-sentence": {
      return { kind: "word-pronunciation-in-sentence", pinyin, word };
    }
  }
}

/**
 * The inverse of {@link cardContentKindFromWordCardKind}. Returns `null` for a grapheme or quiz
 * kind, which has no word-card column value — callers use that to mean "this card does not live in
 * `user_word_sentence_card`".
 */
export function wordCardKindFromCardContentKind(kind: CardContentKind): WordCardKind | null {
  switch (kind) {
    case "grapheme-meaning":
    case "grapheme-pronunciation":
    case "quiz": {
      return null;
    }
    case "word-characters-in-context": {
      return "characters-in-context";
    }
    case "word-characters-in-sentence": {
      return "characters-in-sentence";
    }
    case "word-pronunciation-in-sentence": {
      return "pronunciation-in-sentence";
    }
  }
}
